import {useEffect} from 'react';
// eslint-disable-next-line no-restricted-imports
import {
  useQuery as useQueryOriginal,
  useMutation as useMutationOriginal,
  useQueryClient as useQueryClientOriginal,
  QueryKey,
  QueryFunction,
  UseQueryOptions,
  UseQueryResult,
  MutationFunction,
  UseMutationOptions,
  UseMutationResult,
  QueryObserver,
  QueryClient,
  QueryClientProvider as OriginalQueryClientProvider,
} from 'react-query';
import {isValidationError} from './throwable';
import {useUpdateQueryCache} from './useUpdateQueryCache';

// eslint-disable-next-line no-restricted-imports
export type {UseQueryOptions, QueryObserverResult} from 'react-query';

export const QueryClientProvider = OriginalQueryClientProvider as React.FC<
  React.PropsWithChildren<Parameters<typeof OriginalQueryClientProvider>[0]>
>;

export type UseQueryResultWithUpdateCache<TData, TError> = UseQueryResult<
  TData,
  TError
> & {
  updateCache: (data: TData) => void;
};

export const defaultRetry = (failureCount: number, error: unknown) => {
  if (isValidationError(error)) {
    return false;
  }
  return failureCount <= 0;
};

const TWENTY_MINUTES_IN_MS = 1000 * 60 * 20;
export const DEFAULT_QUERY_CONFIG = {
  keepPreviousData: true,
  retry: defaultRetry,
  retryDelay: 1500,
  staleTime: TWENTY_MINUTES_IN_MS,
  cacheTime:
    process.env.NODE_ENV === 'test'
      ? // 5 seconds for tests makes the test to quit after 5 seconds, otherwise the test hangs until the cache expires
        // https://github.com/TanStack/query/issues/1847
        5000
      : TWENTY_MINUTES_IN_MS,
  refetchOnWindowFocus: false,
};

export const useQuery = <
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
>(
  queryKey: QueryKey,
  queryFunction: QueryFunction<TQueryFnData>,
  config?: UseQueryOptions<TQueryFnData, TError, TData>,
): UseQueryResultWithUpdateCache<TData, TError> => {
  if (!process.browser) {
    return {
      data: undefined,
      error: null,
      isError: false,
      isIdle: false,
      isLoading: true,
      isLoadingError: false,
      isRefetchError: false,
      isSuccess: false,
      status: 'loading',
      updateCache: updateCacheServer,
    } as unknown as UseQueryResultWithUpdateCache<TData, TError>;
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const updateCache = useUpdateQueryCache<TData>({key: queryKey as string});

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const query = useQueryOriginal(queryKey, queryFunction, {
    ...DEFAULT_QUERY_CONFIG,
    ...config,
  }) as UseQueryResultWithUpdateCache<TData, TError>;

  query.updateCache = updateCache;

  return query;
};

interface ReducedUseMutationResult<TData, TError, TVariables, TContext> {
  mutateAsync: UseMutationResult<
    TData,
    TError,
    TVariables,
    TContext
  >['mutateAsync'];
  isLoading: UseMutationResult<
    TData,
    TError,
    TVariables,
    TContext
  >['isLoading'];
  isSuccess: UseMutationResult<
    TData,
    TError,
    TVariables,
    TContext
  >['isSuccess'];
  isError: UseMutationResult<TData, TError, TVariables, TContext>['isError'];
  reset: UseMutationResult<TData, TError, TVariables, TContext>['reset'];
}

export const useMutation = <
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(
  mutationFunction: MutationFunction<TData, TVariables>,
  config?: UseMutationOptions<TData, TError, TVariables, TContext>,
): ReducedUseMutationResult<TData, TError, TVariables, TContext> => {
  if (!process.browser) {
    return {
      mutateAsync: () => {
        throw new Error(
          'Data fetcher query mutations are not supported on the server',
        );
      },
      isLoading: false,
      isSuccess: false,
      isError: false,
      reset: () => null,
    };
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useMutationOriginal(mutationFunction, config);
};

export interface ReducedQueryClient {
  getQueryCache: QueryClient['getQueryCache'];
  getQueryData: QueryClient['getQueryData'];
  setQueryData: QueryClient['setQueryData'];
  invalidateQueries: QueryClient['invalidateQueries'];
  removeQueries: QueryClient['removeQueries'];
  fetchQuery: QueryClient['fetchQuery'];
  setQueryDefaults: QueryClient['setQueryDefaults'];
}

export const useQueryClient = (): ReducedQueryClient => {
  if (!process.browser) {
    return {
      getQueryCache: () => {
        throw new Error(
          'Data fetcher query client is not supported on the server',
        );
      },
      setQueryData: () => {
        throw new Error(
          'Data fetcher query client is not supported on the server',
        );
      },
      getQueryData: () => {
        throw new Error(
          'Data fetcher query client is not supported on the server',
        );
      },
      invalidateQueries: () => {
        throw new Error(
          'Data fetcher query client is not supported on the server',
        );
      },
      fetchQuery: () => {
        throw new Error(
          'Data fetcher query client is not supported on the server',
        );
      },
      removeQueries: () => {
        throw new Error(
          'Data fetcher query client is not supported on the server',
        );
      },
      setQueryDefaults: () => {
        throw new Error(
          'Data fetcher query client is not supported on the server',
        );
      },
    };
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useQueryClientOriginal();
};

export const useQueryDataEffect = <T>(key: string, cb: (result: T) => void) => {
  const queryClient = useQueryClient();
  useEffect(() => {
    let lastState: T | undefined;
    const observer = new QueryObserver<T>(queryClient as QueryClient, {
      queryKey: key,
      queryFn: () => undefined!,
    });

    return observer.subscribe(result => {
      if (result.data !== undefined && lastState !== result.data) {
        cb(result.data);
        lastState = result.data;
      }
    });
  }, [key, queryClient, cb]);
};

export const updateCacheServer = () => {
  throw new Error('updateCache is not expected to be called on the server');
};
export const refetchServer = () => {
  throw new Error('refetch is not expected to be called on the server');
};
